<template>
  <div class="content">
    <div class="content-header">
      <button class="content-header__exit" @click="logout">Выйти</button>
      <RouterLink :to="{name: 'timetable'}" class="content-header__close"><i class="fas fa-times"></i></RouterLink>
    </div>
    <div class="content-header__title">Тест ГИМС</div>
    <div class="content-main">
      <div class="content-main_desc">
        <div v-if="isLoading" class="general-testing-state">Загрузка...</div>
        <div v-else-if="blocks.length === 0" class="general-testing-state">
          Блоки тестирования пока не найдены
        </div>
        <div v-else class="general-testing-list">
          <RouterLink v-if="resumeTesting" :to="resumeTesting.path" class="general-testing-card general-testing-card--resume">
            <div class="general-testing-card__featured-content general-testing-card__featured-content--resume">
              <div>
                <div class="general-testing-card__eyebrow">Последнее тестирование</div>
                <div class="general-testing-card__title">Продолжить тестирование</div>
                <div v-if="resumeTesting.title" class="general-testing-card__resume-text">{{ resumeTesting.title }}</div>
              </div>
              <div class="general-testing-card__cta">
                <span>Продолжить</span>
                <i class="fas fa-arrow-right"></i>
              </div>
            </div>
          </RouterLink>

          <RouterLink :to="{name: 'final-testing'}" class="general-testing-card general-testing-card--featured">
            <div class="general-testing-card__featured-content">
              <div class="general-testing-card__title">Итоговое тестирование</div>
              <div class="general-testing-card__cta">
                <span>Перейти</span>
                <i class="fas fa-arrow-right"></i>
              </div>
            </div>
          </RouterLink>

          <RouterLink
            v-for="block in blocks"
            :key="block.id"
            :to="{name: 'general-testing-block', params: {id: block.id}}"
            class="general-testing-card"
          >
            <div class="general-testing-card__head">
              <div class="general-testing-card__title">{{ block.title }}</div>
              <div class="general-testing-card__arrow"><i class="fas fa-chevron-right"></i></div>
            </div>
            <div class="general-testing-card__stats">
              <div class="general-testing-card__stat">
                <span class="general-testing-card__label">Всего вопросов</span>
                <strong>{{ block.questionsCount }}</strong>
              </div>
              <div class="general-testing-card__stat">
                <span class="general-testing-card__label">Пройдено</span>
                <strong>{{ block.completedQuestionsCount }}</strong>
              </div>
            </div>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {inject, onMounted, ref} from "@vue/runtime-core";
import {getGeneralTesting} from "@/data/requests";

const RECENT_TESTING_KEY = "cmp_recent_testing";

const getCount = (block, keys) => {
  for (const key of keys) {
    if (typeof block?.[key] === 'number') return block[key];
  }

  for (const key of keys) {
    if (Array.isArray(block?.[key])) return block[key].length;
  }

  return 0;
};

const normalizeBlock = (block, index) => {
  return {
    id: block?.id ?? index,
    title: block?.title ?? block?.name ?? block?.label ?? `Блок ${index + 1}`,
    questionsCount: getCount(block, [
      'questions_count',
      'questionsCount',
      'total_questions_count',
      'totalQuestionsCount',
      'questions'
    ]),
    completedQuestionsCount: getCount(block, [
      'completed_questions_count',
      'completedQuestionsCount',
      'passed_questions_count',
      'passedQuestionsCount',
      'completed_questions',
      'completedQuestions',
      'passed_questions',
      'passedQuestions'
    ])
  };
};

function readRecentTesting() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(RECENT_TESTING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const isValidPath = /^\/(general-testing\/\d+\/test\/\d+|final-testing\/\d+)$/.test(parsed?.path ?? "");
    if (!isValidPath) {
      window.localStorage.removeItem(RECENT_TESTING_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
}

export default {
  setup() {
    const logout = inject('logout');
    const token = inject('token');
    const blocks = ref([]);
    const isLoading = ref(true);
    const resumeTesting = ref(null);

    async function loadData() {
      isLoading.value = true;
      const response = await getGeneralTesting(token.value);
      const rawBlocks = Array.isArray(response)
        ? response
        : response?.blocks ?? response?.data ?? [];

      blocks.value = rawBlocks.map(normalizeBlock);
      resumeTesting.value = readRecentTesting();
      isLoading.value = false;
    }

    onMounted(loadData);

    return {logout, blocks, isLoading, resumeTesting};
  }
}
</script>

<style scoped lang="scss">
.content-main_desc {
  max-width: 900px;
  width: 100%;
}

.general-testing-state {
  margin-top: 20px;
  font-size: 16px;
}

.general-testing-list {
  display: grid;
  gap: 16px;
  margin-top: 20px;
}

.general-testing-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  color: inherit;
  text-decoration: none;
}

.general-testing-card--featured,
.general-testing-card--resume {
  padding: 0;
  overflow: hidden;
  color: white;
}

.general-testing-card--featured {
  background: linear-gradient(135deg, #0f4c81 0%, #0a7abf 48%, #11b4a6 100%);
}

.general-testing-card--resume {
  background: linear-gradient(135deg, #bf5e1b 0%, #e38b27 48%, #f0b25d 100%);
}

.general-testing-card__featured-content {
  min-height: 148px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
  background: radial-gradient(circle at top right, rgba(255,255,255,.18), transparent 28%);
}

.general-testing-card__featured-content--resume {
  min-height: 132px;
}

.general-testing-card__eyebrow {
  margin-bottom: 10px;
  font-size: 12px;
  letter-spacing: .1em;
  text-transform: uppercase;
  opacity: .82;
}

.general-testing-card__resume-text {
  margin-top: 10px;
  color: rgba(255,255,255,.88);
  line-height: 1.5;
}

.general-testing-card__head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.general-testing-card__title {
  font-size: 22px;
  line-height: 1.3;
}

.general-testing-card__cta {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .16);
  color: white;
  font-weight: 700;
  letter-spacing: .02em;
}

.general-testing-card__arrow {
  color: #00669c;
  flex: 0 0 auto;
  margin-top: 4px;
}

.general-testing-card__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.general-testing-card__stat {
  background: #f5f9fc;
  border-radius: 10px;
  padding: 16px;
}

.general-testing-card__label {
  display: block;
  font-size: 13px;
  color: #6b7a83;
  margin-bottom: 8px;
}

.general-testing-card__stat strong {
  font-size: 26px;
  color: #00669c;
}

@media all and (max-width: 768px) {
  .general-testing-card {
    padding: 18px;
  }

  .general-testing-card--featured,
  .general-testing-card--resume {
    padding: 0;
  }

  .general-testing-card__featured-content {
    padding: 22px;
    min-height: 132px;
  }

  .general-testing-card__stats {
    grid-template-columns: 1fr;
  }
}
</style>

